import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../api';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { RefreshCw, CheckCircle2, AlertTriangle, Clock, X } from 'lucide-react';
import { toast } from 'sonner';

const fmtGBP  = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(n || 0);
const fmtAbs  = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(Math.abs(n || 0));

const TX_TYPE_LABEL = {
    card_payment:    'Card',
    faster_payment:  'Bank transfer',
    monzo_transfer:  'Monzo transfer',
    declined:        'Declined',
};

const STATUS_CONFIG = {
    reconciled:     { label: 'Matched',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    review_needed:  { label: 'Review needed', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
    pending:        { label: 'Unmatched',     cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    not_applicable: { label: 'Debit',         cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

export default function MonzoTransactions() {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [filter, setFilter] = useState('all');
    const [selected, setSelected] = useState(null);
    const [suggestions, setSuggestions] = useState(null);
    const [loadingSug, setLoadingSug] = useState(false);

    const loadStatus = useCallback(async () => {
        try { const r = await api.get('/auth/monzo/status'); setStatus(r.data); } catch {}
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (filter === 'credits')   params.credit_only = true;
            if (filter === 'unmatched') params.unmatched_only = true;
            const r = await api.get('/monzo/transactions', { params });
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load'); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { loadStatus(); }, [loadStatus]);
    useEffect(() => { if (status?.connected) load(); }, [status, load]);

    const sync = async () => {
        setSyncing(true);
        try {
            const r = await api.post('/monzo/sync');
            toast.success(`Synced — ${r.data.stored} new transactions stored (${r.data.fetched} fetched)`);
            await loadStatus();
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Sync failed'); }
        finally { setSyncing(false); }
    };

    const deepSync = async () => {
        if (!confirm('Deep resync pulls the last 180 days from Monzo, ignoring local history. This is useful if you missed transactions on previous syncs. Continue?')) return;
        setSyncing(true);
        try {
            const r = await api.post('/monzo/sync?deep=true&days=180');
            toast.success(`Deep sync — ${r.data.stored} new of ${r.data.fetched} fetched`);
            await loadStatus();
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Sync failed'); }
        finally { setSyncing(false); }
    };

    const openSuggestions = async (tx) => {
        setSelected(tx);
        setSuggestions(null);
        setLoadingSug(true);
        try {
            const r = await api.get(`/monzo/suggestions/${tx.id}`);
            setSuggestions(r.data.suggestions);
        } catch { setSuggestions([]); }
        finally { setLoadingSug(false); }
    };

    const reconcile = async (tx, invoiceId) => {
        try {
            await api.post(`/monzo/reconcile/${tx.id}`, { invoice_id: invoiceId });
            toast.success('Matched — payment recorded on invoice');
            setSelected(null);
            setSuggestions(null);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Match failed'); }
    };

    if (status && !status.connected) {
        return (
            <div data-testid="monzo-page" className="bm-fade-in">
                <PageHeader title="Monzo" description="Connect your Monzo Business account to auto-reconcile payments." />
                <div className="px-8 py-10 max-w-lg">
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-8 text-center">
                        <div className="w-14 h-14 rounded-full bg-[#FF3B30]/10 flex items-center justify-center mx-auto mb-4">
                            <span className="text-[22px] font-bold text-[#FF3B30]">M</span>
                        </div>
                        <div className="font-display text-[18px] font-semibold mb-2">Connect Monzo Business</div>
                        <div className="text-[13px] text-[color:var(--bm-text-muted)] mb-6">
                            Pull in all transactions and auto-reconcile incoming payments against your invoices.
                        </div>
                        <Button onClick={() => navigate('/settings')} style={{ background: 'var(--bm-primary)' }} className="h-10">
                            Go to Settings to connect
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const stats  = data?.stats;
    const txList = data?.transactions || [];

    return (
        <div data-testid="monzo-page" className="bm-fade-in">
            <PageHeader
                title="Monzo"
                description={status?.last_sync_at
                    ? `Last synced ${new Date(status.last_sync_at).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}`
                    : 'Transaction feed from your Monzo Business account.'}
                actions={
                    <div className="flex items-center gap-2">
                        <Button onClick={deepSync} disabled={syncing} variant="outline" className="h-9" data-testid="monzo-deep-sync-btn">
                            <RefreshCw size={14} className={`mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                            Deep resync (180 days)
                        </Button>
                        <Button onClick={sync} disabled={syncing} variant="outline" className="h-9" data-testid="monzo-sync-btn">
                            <RefreshCw size={14} className={`mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                            {syncing ? 'Syncing…' : 'Sync now'}
                        </Button>
                    </div>
                }
            />

            <div className="px-8 py-6 space-y-5">
                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                            { label: 'Credits this month', value: stats.credits_this_month,  cls: '' },
                            { label: 'Auto-reconciled',    value: stats.reconciled,          cls: 'text-emerald-600' },
                            { label: 'Unmatched credits',  value: stats.unmatched,           cls: 'text-indigo-600' },
                            { label: 'Review needed',      value: stats.review_needed,       cls: 'text-amber-600' },
                        ].map(s => (
                            <div key={s.label} className="bg-white rounded-xl border border-[color:var(--bm-border)] px-5 py-4">
                                <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{s.label}</div>
                                <div className={`font-display text-[24px] font-semibold mt-1 ${s.cls || 'text-[color:var(--bm-text)]'}`}>{s.value}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Filter tabs */}
                <div className="flex gap-1 border-b border-[color:var(--bm-border)]">
                    {[
                        { key: 'all',       label: `All (${txList.length})` },
                        { key: 'credits',   label: 'Credits' },
                        { key: 'unmatched', label: 'Unmatched' },
                    ].map(t => (
                        <button key={t.key} onClick={() => setFilter(t.key)}
                            className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition -mb-px ${filter === t.key ? 'border-[color:var(--bm-primary)] text-[color:var(--bm-primary)]' : 'border-transparent text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)]'}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Transaction table */}
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-x-auto">
                    <table className="w-full text-[12.5px]">
                        <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                            <tr>
                                <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Date</th>
                                <th className="text-left font-medium px-4 py-3">Description</th>
                                <th className="text-left font-medium px-3 py-3 whitespace-nowrap">Type</th>
                                <th className="text-left font-medium px-3 py-3">Category</th>
                                <th className="text-right font-medium px-3 py-3 whitespace-nowrap">Amount</th>
                                <th className="text-right font-medium px-3 py-3 whitespace-nowrap">Balance</th>
                                <th className="text-left font-medium px-3 py-3">Status</th>
                                <th className="px-3 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={8} className="px-4 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>
                            )}
                            {!loading && txList.length === 0 && (
                                <tr><td colSpan={8} className="px-4 py-12 text-center text-[color:var(--bm-text-muted)]">
                                    No transactions found. Click "Sync now" to pull from Monzo.
                                </td></tr>
                            )}
                            {txList.map(tx => {
                                const st     = STATUS_CONFIG[tx.reconcile_status] || STATUS_CONFIG.not_applicable;
                                const credit = tx.is_credit;
                                const isSel  = selected?.id === tx.id;
                                const name   = tx.merchant_name || tx.description || '—';
                                const note   = tx.notes || '';
                                return (
                                    <React.Fragment key={tx.id}>
                                        <tr className={`border-t border-[color:var(--bm-border)] ${isSel ? 'bg-indigo-50/50' : 'hover:bg-[color:var(--bm-bg-mute)]'} transition`}
                                            data-testid={`monzo-tx-${tx.id}`}>
                                            <td className="px-4 py-3 text-[color:var(--bm-text-muted)] whitespace-nowrap">
                                                {tx.created ? new Date(tx.created).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'2-digit' }) : '—'}
                                            </td>
                                            <td className="px-4 py-3 max-w-[220px]">
                                                <div className="font-medium text-[color:var(--bm-text)] truncate">{name}</div>
                                                {note && <div className="text-[11px] text-[color:var(--bm-text-subtle)] truncate">{note}</div>}
                                                {tx.matched_invoice_number && (
                                                    <button onClick={() => navigate(`/invoices/${tx.matched_invoice_id}`)}
                                                        className="text-[11px] text-[color:var(--bm-primary)] hover:underline">
                                                        → {tx.matched_invoice_number}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-[color:var(--bm-text-muted)] whitespace-nowrap">
                                                {TX_TYPE_LABEL[tx.transaction_type] || tx.transaction_type || '—'}
                                            </td>
                                            <td className="px-3 py-3 text-[color:var(--bm-text-muted)] capitalize">
                                                {tx.category || '—'}
                                            </td>
                                            <td className={`px-3 py-3 text-right font-semibold whitespace-nowrap ${credit ? 'text-emerald-600' : 'text-[color:var(--bm-text)]'}`}>
                                                {credit ? '+' : '−'}{fmtAbs(tx.amount_gbp)}
                                            </td>
                                            <td className="px-3 py-3 text-right text-[color:var(--bm-text-muted)] whitespace-nowrap">
                                                {tx.balance_gbp != null ? fmtGBP(tx.balance_gbp) : '—'}
                                            </td>
                                            <td className="px-3 py-3">
                                                <span className={`bm-pill border text-[11px] ${st.cls}`}>{st.label}</span>
                                            </td>
                                            <td className="px-3 py-3 text-right">
                                                {credit && !tx.reconciled && (
                                                    <button onClick={() => isSel ? setSelected(null) : openSuggestions(tx)}
                                                        className="text-[12px] text-[color:var(--bm-primary)] hover:underline whitespace-nowrap"
                                                        data-testid={`monzo-match-${tx.id}`}>
                                                        {isSel ? 'Close' : 'Match'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>

                                        {/* Inline reconciliation panel */}
                                        {isSel && (
                                            <tr className="border-t border-indigo-100">
                                                <td colSpan={8} className="px-5 py-4 bg-indigo-50/40">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="text-[12.5px] font-semibold text-[color:var(--bm-text)]">
                                                            Match {fmtAbs(tx.amount_gbp)} from "{name}" to an invoice
                                                        </div>
                                                        <button onClick={() => setSelected(null)}><X size={14} className="text-[color:var(--bm-text-subtle)]" /></button>
                                                    </div>
                                                    {loadingSug && <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">Finding matches…</div>}
                                                    {!loadingSug && suggestions?.length === 0 && (
                                                        <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">No outstanding invoices within £5 of this amount.</div>
                                                    )}
                                                    {!loadingSug && suggestions?.length > 0 && (
                                                        <div className="space-y-2">
                                                            {suggestions.map(inv => (
                                                                <div key={inv.id} className="flex items-center justify-between bg-white border border-[color:var(--bm-border)] rounded-lg px-4 py-2.5">
                                                                    <div>
                                                                        <div className="font-medium text-[13px]">{inv.invoice_number}</div>
                                                                        <div className="text-[11.5px] text-[color:var(--bm-text-muted)]">{inv.client_name} · balance {fmtGBP(inv.balance_due)} · due {inv.due_date}</div>
                                                                    </div>
                                                                    <Button size="sm" onClick={() => reconcile(tx, inv.id)}
                                                                        className="h-7 text-[11.5px] ml-4" style={{ background: 'var(--bm-primary)' }}
                                                                        data-testid={`monzo-confirm-match-${inv.id}`}>
                                                                        Confirm match
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
